import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useCheckSubmissionStatus } from "@/hooks/useSelfService";
import { format } from "date-fns";
import {
  CheckCircle2,
  CircleAlert,
  Clock,
  Loader2,
  RotateCcw,
  SearchX,
} from "lucide-react";
import { useEffect, useState } from "react";
import { navigate } from "wouter/use-browser-location";
import logo from "/asset/logo-removebg-preview.png";
import {
  DEFAULT_DECIMAL_PRECISION,
  format as formatAtPrecision,
} from "@/utils/decimal";

const SelfServiceCheckStatusPage = () => {
  const params = new URLSearchParams(window.location.search);
  const codeFromUrl = params.get("code")?.toUpperCase() ?? "";

  const [inputCode, setInputCode] = useState(codeFromUrl);
  const [submittedCode, setSubmittedCode] = useState(codeFromUrl);

  const { data, isFetching, isError, refetch } =
    useCheckSubmissionStatus(submittedCode);

  // Keep input in sync when coming from URL
  useEffect(() => {
    if (codeFromUrl) {
      setInputCode(codeFromUrl);
      setSubmittedCode(codeFromUrl);
    }
  }, [codeFromUrl]);

  const handleCheck = () => {
    const trimmed = inputCode.trim().toUpperCase();
    setSubmittedCode(trimmed);
    if (trimmed === submittedCode) {
      refetch();
    }
  };

  const formatDateTime = (val: string | null | undefined) => {
    if (!val) return "—";
    try {
      return format(new Date(val), "dd MMM yyyy, hh:mm:ss a");
    } catch {
      return val;
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center py-8 relative overflow-hidden bg-gradient-to-br from-blue-600 to-purple-700">
      <div className="absolute inset-0" style={{ opacity: 0.8 }}>
        <svg
          className="w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          role="img"
          aria-labelledby="waveTitle"
        >
          <title id="waveTitle">Animated wave background</title>
          <path
            fill="rgba(59, 130, 246, 0.2)"
            d="M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z"
          >
            <animate
              attributeName="d"
              dur="10s"
              repeatCount="indefinite"
              values="
                M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z;
                M-150 30 C-100 40, -50 50, 0 30 S 50 40, 100 50 S 150 40, 200 30 V100 H-150 Z;
                M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z"
            />
          </path>
          <path
            fill="rgba(139, 92, 246, 0.2)"
            d="M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z"
          >
            <animate
              attributeName="d"
              dur="8s"
              repeatCount="indefinite"
              values="
                M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z;
                M-150 40 C-100 50, -50 60, 0 40 S 50 50, 100 60 S 150 50, 200 40 V100 H-150 Z;
                M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z"
            />
          </path>
          <path
            fill="rgba(99, 102, 241, 0.2)"
            d="M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z"
          >
            <animate
              attributeName="d"
              dur="6s"
              repeatCount="indefinite"
              values="
                M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z;
                M-150 50 C-100 60, -50 70, 0 50 S 50 60, 100 70 S 150 60, 200 50 V100 H-150 Z;
                M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z"
            />
          </path>
        </svg>

        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={`particle-${i}`}
              className="absolute rounded-full bg-white/10 animate-float"
              style={{
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 10 + 10}s`,
              }}
            />
          ))}
        </div>
      </div>

      <Card className="w-[95%] lg:w-[520px] bg-white/90 backdrop-blur-sm shadow-xl relative z-10 p-1">
        <CardHeader className="gap-2">
          <CardTitle className="flex items-center justify-center">
            <img className="h-16" src={logo} />
          </CardTitle>
          <CardDescription className="text-black text-center">
            Check your e-invoice submission status
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Code input */}
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="statusCode">Status Check Code</Label>
              <Input
                id="statusCode"
                placeholder="e.g. XJ12Y3"
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                maxLength={8}
                className="font-mono tracking-widest uppercase"
                onKeyDown={(e) => e.key === "Enter" && handleCheck()}
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleCheck}
                disabled={isFetching || inputCode.trim().length !== 8}
              >
                {isFetching ? <Loader2 className="animate-spin" /> : "Check"}
              </Button>
            </div>
          </div>

          {/* Result area */}
          {submittedCode && !isFetching && (
            <>
              <Separator />
              {isError ? (
                <div className="flex flex-col items-center gap-3 text-gray-400 py-4">
                  <SearchX size={48} />
                  <p className="text-sm text-center">
                    No record found for code{" "}
                    <span className="font-mono font-bold text-gray-600">
                      {submittedCode}
                    </span>
                    . Please double-check the code and try again.
                  </p>
                </div>
              ) : data ? (
                <StatusResult
                  data={data}
                  formatDateTime={formatDateTime}
                  onRefresh={refetch}
                  isRefreshing={isFetching}
                  onFixInvoice={() =>
                    navigate(`/self-service/${data.invoiceId}`)
                  }
                />
              ) : null}
            </>
          )}

          <Separator />
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.history.back()}
            >
              Back to Self-Service Portal
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

type StatusData = {
  statusDisplay: "Processing" | "Valid" | "Invalid";
  status: string | null;
  statusCheckCode: string;
  supplierName: string;
  totalPayableAmount: number;
  originalInvoiceNumber: string | null;
  invoiceId: string;
  submittedAt: string | null;
  validatedAt: string | null;
  errorDetails: any;
};

const StatusResult = ({
  data,
  formatDateTime,
  onRefresh,
  isRefreshing,
  onFixInvoice,
}: {
  data: StatusData;
  formatDateTime: (val: string | null | undefined) => string;
  onRefresh: () => void;
  isRefreshing: boolean;
  onFixInvoice: () => void;
}) => {
  const formatDecimal = (v: number | null | undefined) =>
    formatAtPrecision(Number(v ?? 0), DEFAULT_DECIMAL_PRECISION);
  const statusConfig = {
    Processing: {
      icon: <Clock size={40} className="text-blue-400 animate-pulse" />,
      label: "Processing",
      labelClass: "text-blue-600",
      bgClass: "bg-blue-50 border-blue-200",
      message:
        "Your invoice submission is still being processed by LHDN. This may take a few minutes. Please check again later.",
    },
    Valid: {
      icon: <CheckCircle2 size={40} className="text-green-500" />,
      label: "Valid",
      labelClass: "text-green-700",
      bgClass: "bg-green-50 border-green-200",
      message:
        "Your invoice has been successfully validated by LHDN. No further action is required.",
    },
    Invalid: {
      icon: <CircleAlert size={40} className="text-red-500" />,
      label: "Invalid",
      labelClass: "text-red-700",
      bgClass: "bg-red-50 border-red-200",
      message:
        "Your invoice was rejected by LHDN. Please review the error details below, fix the issues, and resubmit.",
    },
  };

  const config = statusConfig[data.statusDisplay];

  return (
    <div className="space-y-4">
      {/* Status badge */}
      <div
        className={`flex items-center gap-3 rounded-lg border p-4 ${config.bgClass}`}
      >
        {config.icon}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
            Submission Status
          </p>
          <p className={`text-xl font-bold ${config.labelClass}`}>
            {config.label}
          </p>
          <p className="text-sm text-gray-600 mt-1">{config.message}</p>
        </div>
      </div>

      {/* Invoice details */}
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Supplier</span>
          <span className="font-medium text-right max-w-[60%]">
            {data.supplierName}
          </span>
        </div>
        {data.originalInvoiceNumber && (
          <div className="flex justify-between">
            <span className="text-gray-500">POS Reference</span>
            <span className="font-medium">{data.originalInvoiceNumber}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-500">Total Amount</span>
          <span className="font-medium">
            RM {formatDecimal(Number(data.totalPayableAmount))}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Submitted Time</span>
          <span className="font-medium">
            {formatDateTime(data.submittedAt)}
          </span>
        </div>
        {data.validatedAt && (
          <div className="flex justify-between">
            <span className="text-gray-500">Validated Time</span>
            <span className="font-medium">
              {formatDateTime(data.validatedAt)}
            </span>
          </div>
        )}
      </div>

      {/* Error details for Invalid */}
      {data.statusDisplay === "Invalid" && data.errorDetails && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-1">
          <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">
            Error Details
          </p>
          <pre className="text-xs text-red-600 whitespace-pre-wrap break-all">
            {typeof data.errorDetails === "string"
              ? data.errorDetails
              : JSON.stringify(data.errorDetails, null, 2)}
          </pre>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 pt-1">
        {data.statusDisplay === "Processing" && (
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing}>
            {isRefreshing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RotateCcw size={14} className="mr-1" />}
            Refresh
          </Button>
        )}
        {data.statusDisplay === "Invalid" && (
          <Button size="sm" onClick={onFixInvoice}>
            Fix & Resubmit Invoice
          </Button>
        )}
      </div>
    </div>
  );
};

export default SelfServiceCheckStatusPage;
